import Configuration from "config";
import moment from "moment-timezone";
import { bindConsent, onConsentKnown } from "services/cookieConsent";
import type { User } from "services/users/model";

type ConsentValue = "granted" | "denied";

// The container's ad tags fire for the first two only
// (tools/gtm/create-app-container.py): employers' contracts rule out advertising
// trackers for company learners, and staff would pollute the audiences.
export type TagAudience = "anonymous" | "consumer" | "company" | "staff";

export const AUDIENCE_EVENT = "talaera_audience";

// The container runs whatever script its publishers add, so it loads only on a visit
// that lands on an offer page, or on a setup page reloaded part-way through.
const FUNNEL_PATH_PREFIXES = ["/start/", "/setup/"];

const SCRIPT_ID = "google-tag-manager";
let scriptInjected = false;
let pendingAudience: TagAudience | undefined;

export function pushDataLayer(event: string, params?: Record<string, unknown>) {
  const layer = dataLayer();
  // GTM merges every push into the state it keeps, so a `purchase` would inherit
  // whatever `begin_checkout` left under `ecommerce`; Google's guidance is to clear it first.
  if (params && "ecommerce" in params) {
    layer.push({ ecommerce: null });
  }
  layer.push({ event, ...params });
}

/**
 * Google Tag Manager in Consent Mode v2, the "advanced" form the website uses:
 * the consent signals start denied and the container loads straight away, so
 * its tags run without cookies or identifiers until the visitor's choice is
 * known. `bindConsent` then pushes the update for each category as Cookiebot
 * reports it, on load and on every change. Google models what the denied period
 * lost, which is how EU traffic still counts.
 */
export function initTagManager(landingPath: string = window.location.pathname) {
  const containerId = Configuration.GTM_ID;
  if (
    !containerId ||
    !FUNNEL_PATH_PREFIXES.some(prefix => landingPath.startsWith(prefix))
  ) {
    return;
  }

  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    // Gives Cookiebot this long to report a stored choice before tags decide.
    wait_for_update: 500,
  });

  bindConsent(
    "marketing",
    () => updateAdConsent("granted"),
    () => updateAdConsent("denied")
  );
  bindConsent(
    "statistics",
    () => gtag("consent", "update", { analytics_storage: "granted" }),
    () => gtag("consent", "update", { analytics_storage: "denied" })
  );

  injectScript(containerId);
}

/**
 * Pushes the audience only once the consent state is known: the ad tags require
 * consent, and GTM skips (never queues) a consent-gated tag whose trigger
 * fires before Cookiebot has reported the visitor's choice. A call made during
 * that wait replaces the audience instead of adding a push, so a visitor who
 * signs in before answering the banner is never announced as anonymous.
 *
 * `undefined` is a session not yet known, or one whose fetch failed. Only `null`
 * is a confirmed anonymous visitor, and the unknown case pushes nothing: GTM
 * replays the queue in order, so an early guess of "anonymous" would fire the
 * pixel on a company learner's page.
 */
export function setTagAudience(user: User | null | undefined) {
  if (user === undefined) {
    return;
  }
  const alreadyWaiting = pendingAudience !== undefined;
  pendingAudience = audienceOf(user);
  if (alreadyWaiting) {
    return;
  }
  onConsentKnown(() => {
    pushDataLayer(AUDIENCE_EVENT, { audience: pendingAudience });
    pendingAudience = undefined;
  });
}

export function audienceOf(user: User | null): TagAudience {
  if (!user) {
    return "anonymous";
  }
  if (user.company) {
    return "company";
  }
  return user.roles.includes("student") ? "consumer" : "staff";
}

function updateAdConsent(value: ConsentValue) {
  gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
}

function injectScript(containerId: string) {
  if (scriptInjected) {
    return;
  }
  scriptInjected = true;

  dataLayer().push({ "gtm.start": moment.utc().valueOf(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

// Consent Mode reads `arguments` objects off the queue, not arrays — the shape
// Google's own snippet pushes, and the one GTM's consent handling recognises.
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params -- see above
  dataLayer().push(arguments);
}

function dataLayer() {
  window.dataLayer = window.dataLayer ?? [];
  // A tag template may look for the global.
  window.gtag = window.gtag ?? gtag;
  return window.dataLayer;
}
